import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError, fileDownloadUrl } from "@/lib/api";
import { useT } from "@/i18n";
import { useCenter } from "@/context/CenterContext";
import UploadButton from "@/components/UploadButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, MapPin, Users, Clock, Trash2, FileText, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import {
  startOfWeek, addDays, format, addMonths, addWeeks, isSameDay, parseISO, startOfMonth, endOfMonth,
  eachDayOfInterval, isSameMonth, endOfWeek,
} from "date-fns";

const TYPE_COLORS = { appointment: "#EF4444", meeting: "#3B82F6", other: "#F59E0B" };
const fmtD = (d) => format(d, "yyyy-MM-dd");

function EventDialog({ open, onOpenChange, event, employees, residents, defaultDate, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open) setForm(event || { title: "", type: "appointment", start: `${defaultDate || fmtD(new Date())}T09:00`, end: `${defaultDate || fmtD(new Date())}T10:00`, resident_id: "", assignees: [], location: "", notes: "", attachments: [], color: "#3B82F6", reminder_minutes: 60 });
  }, [open, event, defaultDate]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...form, color: TYPE_COLORS[form.type] || form.color };
      if (!payload.resident_id) payload.resident_id = null;
      if (event) await api.put(`/calendar/events/${event.id}`, payload); else await api.post("/calendar/events", payload);
      toast.success(t("save")); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => { await api.delete(`/calendar/events/${event.id}`); toast.success(t("delete")); onSaved(); onOpenChange(false); };
  const toggleAssignee = (id) => set("assignees", (form.assignees || []).includes(id) ? form.assignees.filter((x) => x !== id) : [...(form.assignees || []), id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader><DialogTitle>{event ? t("editEvent") : t("newEvent")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">{t("title")}</Label><Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="event-title" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5"><Label className="text-xs">{t("eventType")}</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="appointment">{t("appointmentType")}</SelectItem><SelectItem value="meeting">{t("meeting")}</SelectItem><SelectItem value="other">{t("other")}</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">{t("resident")}</Label>
              <Select value={form.resident_id || "none"} onValueChange={(v) => set("resident_id", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder={t("none")} /></SelectTrigger>
                <SelectContent><SelectItem value="none">{t("none")}</SelectItem>{residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Start</Label><Input type="datetime-local" value={form.start?.slice(0, 16) || ""} onChange={(e) => set("start", e.target.value)} data-testid="event-start" /></div>
            <div className="space-y-1.5"><Label className="text-xs">End</Label><Input type="datetime-local" value={form.end?.slice(0, 16) || ""} onChange={(e) => set("end", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">{t("location")}</Label><Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs">{t("assignees")}</Label>
            <div className="flex flex-wrap gap-1.5">
              {employees.map((e) => (
                <button key={e.id} type="button" onClick={() => toggleAssignee(e.id)}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${(form.assignees || []).includes(e.id) ? "bg-primary text-white border-primary" : "border-border hover:border-primary"}`}>{e.name}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">{t("notes")}</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          <div className="flex items-center gap-3">
            <UploadButton label={t("uploadFile")} accept="application/pdf,image/*" category="event" testId="event-upload"
              onUploaded={(d) => set("attachments", [...(form.attachments || []), { file_id: d.id, name: d.original_filename }])} />
            {(form.attachments || []).map((a, i) => <a key={i} href={fileDownloadUrl(a.file_id)} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1"><FileText className="w-3 h-3" />{a.name}</a>)}
          </div>
        </div>
        <DialogFooter className="flex-row justify-between">
          {event ? <Button variant="ghost" onClick={del} data-testid="delete-event"><Trash2 className="w-4 h-4 text-destructive" /></Button> : <span />}
          <div className="flex gap-2"><Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button><Button onClick={save} data-testid="save-event">{t("save")}</Button></div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Calendar() {
  const { t, lang } = useT();
  const { isManager } = useCenter();
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [residents, setResidents] = useState([]);
  const [filterEmp, setFilterEmp] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [dialog, setDialog] = useState({ open: false, event: null, date: null });

  const range = view === "month"
    ? { start: startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 }) }
    : view === "week" ? { start: startOfWeek(anchor, { weekStartsOn: 1 }), end: endOfWeek(anchor, { weekStartsOn: 1 }) }
    : view === "day" ? { start: anchor, end: anchor }
    : { start: anchor, end: addDays(anchor, 30) };

  const load = useCallback(async () => {
    const params = { start: fmtD(range.start), end: fmtD(range.end) };
    if (filterEmp !== "all") params.employee_id = filterEmp;
    if (filterType !== "all") params.type = filterType;
    const { data } = await api.get("/calendar/events", { params });
    setEvents(data);
  }, [anchor, view, filterEmp, filterType]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data.filter((e) => e.active))); api.get("/residents").then((r) => setResidents(r.data)).catch(() => {}); }, []);

  const eventsOn = (day) => events.filter((e) => isSameDay(parseISO(e.start), day));
  const nav = (dir) => setAnchor(view === "month" ? addMonths(anchor, dir) : view === "week" ? addWeeks(anchor, dir) : addDays(anchor, dir));
  const openNew = (date) => isManager && setDialog({ open: true, event: null, date: date ? fmtD(date) : fmtD(anchor) });

  const monthDays = eachDayOfInterval({ start: range.start, end: range.end });
  const weekDays = eachDayOfInterval({ start: startOfWeek(anchor, { weekStartsOn: 1 }), end: endOfWeek(anchor, { weekStartsOn: 1 }) });

  const EventChip = ({ e }) => (
    <div onClick={(ev) => { ev.stopPropagation(); isManager && setDialog({ open: true, event: e }); }}
      className="text-[11px] rounded px-1.5 py-0.5 mb-0.5 text-white truncate cursor-pointer" style={{ background: e.color || TYPE_COLORS[e.type] }} data-testid="calendar-event">
      {format(parseISO(e.start), "HH:mm")} {e.title}
    </div>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t("calendar")}</h1>
          <p className="text-muted-foreground mt-1 capitalize">{format(anchor, view === "month" ? "MMMM yyyy" : "MMM d, yyyy")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-lg border border-border overflow-hidden">
            {["day", "week", "month", "agenda"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                className={`px-3 py-1.5 text-sm font-medium capitalize transition-colors ${view === v ? "bg-primary text-white" : "hover:bg-accent"}`}>{t(v)}</button>
            ))}
          </div>
          <Button variant="outline" size="icon" onClick={() => nav(-1)} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())}>{t("today")}</Button>
          <Button variant="outline" size="icon" onClick={() => nav(1)} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          {isManager && <Button size="sm" onClick={() => openNew()} data-testid="add-event-button"><Plus className="w-4 h-4 mr-1" />{t("newEvent")}</Button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 h-9" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allTypes")}</SelectItem><SelectItem value="appointment">{t("appointmentType")}</SelectItem><SelectItem value="meeting">{t("meeting")}</SelectItem><SelectItem value="other">{t("other")}</SelectItem></SelectContent>
        </Select>
        <Select value={filterEmp} onValueChange={setFilterEmp}>
          <SelectTrigger className="w-44 h-9" data-testid="filter-employee"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("allEmployees")}</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {view === "month" && (
        <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden border border-border">
          {weekDays.map((d) => <div key={d} className="bg-muted p-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">{format(d, "EEE")}</div>)}
          {monthDays.map((day) => (
            <div key={day.toISOString()} className={`bg-card min-h-24 p-1.5 ${!isSameMonth(day, anchor) ? "opacity-40" : ""} ${isSameDay(day, new Date()) ? "ring-1 ring-inset ring-primary" : ""}`} onClick={() => openNew(day)} data-testid="cal-month-cell">
              <div className="text-xs font-medium mb-1">{format(day, "d")}</div>
              {eventsOn(day).slice(0, 3).map((e) => <EventChip key={e.id} e={e} />)}
              {eventsOn(day).length > 3 && <div className="text-[10px] text-muted-foreground">+{eventsOn(day).length - 3}</div>}
            </div>
          ))}
        </div>
      )}

      {(view === "week" || view === "day") && (
        <div className={`grid gap-px bg-border rounded-lg overflow-hidden border border-border`} style={{ gridTemplateColumns: `repeat(${view === "day" ? 1 : 7}, 1fr)` }}>
          {(view === "day" ? [anchor] : weekDays).map((day) => (
            <div key={day.toISOString()} className="bg-card min-h-[60vh]" onClick={() => openNew(day)}>
              <div className={`p-2 text-center border-b border-border sticky top-0 bg-muted ${isSameDay(day, new Date()) ? "text-primary" : ""}`}>
                <div className="text-xs uppercase tracking-wider">{format(day, "EEE")}</div>
                <div className="font-display font-bold">{format(day, "d")}</div>
              </div>
              <div className="p-1.5 space-y-1">
                {eventsOn(day).map((e) => (
                  <div key={e.id} onClick={(ev) => { ev.stopPropagation(); isManager && setDialog({ open: true, event: e }); }}
                    className="rounded-lg p-2 text-white text-xs cursor-pointer" style={{ background: e.color || TYPE_COLORS[e.type] }} data-testid="calendar-event">
                    <div className="font-semibold">{format(parseISO(e.start), "HH:mm")} {e.title}</div>
                    {e.resident_name && <div className="opacity-90">{e.resident_name}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "agenda" && (
        <div className="space-y-2">
          {events.length === 0 && <p className="text-center text-muted-foreground py-12">{t("noEvents")}</p>}
          {events.map((e) => (
            <Card key={e.id} className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => isManager && setDialog({ open: true, event: e })} data-testid="agenda-event">
              <div className="w-1 self-stretch rounded-full" style={{ background: e.color || TYPE_COLORS[e.type] }} />
              <div className="text-center shrink-0 w-16">
                <div className="font-display font-bold">{format(parseISO(e.start), "MMM d")}</div>
                <div className="text-xs text-muted-foreground">{format(parseISO(e.start), "HH:mm")}</div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{e.title}</p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
                  <Badge variant="outline" className="text-[10px]">{t(e.type === "appointment" ? "appointmentType" : e.type)}</Badge>
                  {e.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>}
                  {e.resident_name && <span>{e.resident_name}</span>}
                  {e.assignees?.length > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{e.assignees.length}</span>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <EventDialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} event={dialog.event} employees={employees} residents={residents} defaultDate={dialog.date} onSaved={load} />
    </div>
  );
}
